import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import triggersHelper from "../../../../API/Triggers/triggersHelper";
import authorisationsHelper from "../../../../API/Authorisations/authorisationsHelper";
import { loadTriggerCatalog } from "../../../../Constants/triggerCatalog";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import AgentBuilderSidebar from "../../../Agents/Components/AgentBuilderSidebar";
import TriggersSettings from "./TriggersSettings";
import { buildInitialValues, fromTriggerRecord } from "./TriggersList";

const HASH_ROUTED_SECTIONS = ["profile", "execution", "event", "behaviour"];

const TriggerBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const triggerId = params?.triggerId;
  const isEditMode = !!triggerId && triggerId !== "new";
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const { getActiveWorkspace } = useWorkspaces();

  const [activeSection, setActiveSection] = useState("profile");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  const [saveVersion, setSaveVersion] = useState(0);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const [isTriggersLoading, setIsTriggersLoading] = useState(true);
  const createInitRef = useRef(false);
  const editInitRef = useRef(null);

  const loadTriggers = useCallback(
    async (workspaceID) => {
      try {
        setIsTriggersLoading(true);
        let resultTriggers = await triggersHelper.getTriggersByWorkspaceID(
          workspaceID
        );
        resultTriggers =
          resultTriggers?.data?.triggersByWorkspace?.items?.filter(
            (u) => u?._deleted != true
          );
        dispatch("SET_TRIGGERS", resultTriggers || []);
      } catch (error) {
        console.error("Error loading triggers:", error);
        dispatch("SET_TRIGGERS", []);
      } finally {
        setIsTriggersLoading(false);
      }
    },
    [dispatch]
  );

  const loadAuthorisations = useCallback(
    async (workspaceID) => {
      try {
        const authorisationsData = await authorisationsHelper.getAuthorisations(
          workspaceID,
          null
        );
        let authorisations =
          authorisationsData?.data?.authorisationsByWorkspace?.items;
        authorisations =
          authorisations
            ?.filter((u) => u?._deleted !== true)
            ?.map((u) => {
              if (u?.staticArgs) {
                try {
                  return { ...u, staticArgs: JSON.parse(u?.staticArgs) };
                } catch {
                  return u;
                }
              }
              return u;
            }) || [];
        dispatch("SET_AUTHORISATIONS", authorisations || []);
      } catch (error) {
        console.error("Error loading authorisations for triggers:", error);
      }
    },
    [dispatch]
  );

  const loadCatalog = useCallback(async () => {
    try {
      const catalog = await loadTriggerCatalog();
      dispatch("SET_TRIGGER_CATALOG", {
        version: catalog.version,
        nativeEvents: catalog.nativeEvents,
        providers: catalog.providers,
      });
    } catch (error) {
      console.error("Error loading trigger catalog:", error);
    }
  }, [dispatch]);

  useEffect(() => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    const triggers = state?.triggers || [];
    const belongsToWorkspace = triggers.some((u) => u?.workspaceID === wsId);
    if (triggers.length === 0 || !belongsToWorkspace) {
      loadTriggers(wsId);
    } else {
      setIsTriggersLoading(false);
    }
    loadAuthorisations(wsId);
    loadCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    if (!triggerId) {
      history.replace(`/workspaces/${workspaceId}/settings/triggers`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (triggerId !== "new") return;
    if (createInitRef.current) return;
    createInitRef.current = true;
    setInitialMapValues(buildInitialValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    if (isTriggersLoading) return;
    if (editInitRef.current === triggerId) return;
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    const trigger = (state?.triggers || []).find((u) => u?.id === triggerId);
    if (!trigger) {
      const listIsStale = state?.triggers?.some(
        (u) => u?.workspaceID && u?.workspaceID !== activeWorkspaceId
      );
      if (listIsStale) return;
      history.replace(`/workspaces/${activeWorkspaceId}/settings/triggers`);
      return;
    }
    editInitRef.current = triggerId;
    setInitialMapValues(fromTriggerRecord(trigger));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    triggerId,
    isEditMode,
    isTriggersLoading,
    state?.triggers,
    getActiveWorkspace()?.id,
  ]);

  const sectionGroups = useMemo(() => {
    const groups = [
      {
        id: "setup",
        label: t("triggers.builderGroupSetup", { defaultValue: "Set up" }),
        items: [
          {
            id: "profile",
            title: t("triggers.builderSectionProfile", {
              defaultValue: "Overview",
            }),
            icon: AppAssets.icons.persona,
          },
          {
            id: "execution",
            title: t("triggers.builderSectionExecution", {
              defaultValue: "Execution",
            }),
            icon: AppAssets.icons.bolt,
          },
          {
            id: "event",
            title: t("triggers.builderSectionEvent", {
              defaultValue: "Event source",
            }),
            icon: AppAssets.icons.webhook,
          },
        ],
      },
      {
        id: "system",
        label: t("triggers.builderGroupSystem", { defaultValue: "System" }),
        items: [
          {
            id: "behaviour",
            title: t("triggers.builderSectionBehaviour", {
              defaultValue: "Behaviour",
            }),
            icon: AppAssets.icons.launch,
          },
        ],
      },
    ];
    return groups
      .map((g) => ({
        ...g,
        items: g.items,
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (HASH_ROUTED_SECTIONS.includes(raw)) {
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerId, isEditMode, initialMapValues]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/triggers/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("triggers.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const isFormReady =
    initialMapValues != null &&
    !isTriggersLoading &&
    (!isEditMode || initialMapValues?.id === triggerId);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [activeSection]);

  const handleTriggerCreated = (status, isEdit, savedId, submittedValues) => {
    if (status === "success") {
      AppToasty.show(t("triggers.creationSuccess"), { type: "success" });
      formRef?.current?.resetForm?.({
        values: submittedValues || formRef.current?.values,
      });
      setSaveVersion((v) => v + 1);
      if (!isEdit && savedId) {
        createInitRef.current = false;
        history.replace(`/workspaces/${workspaceId}/triggers/${savedId}`);
      }
    } else {
      AppToasty.show(t("triggers.creationFailed"), { type: "danger" });
    }
  };

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={() => formRef?.current?.submitForm()}
      isDisabled={!isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const selectedTrigger = useMemo(
    () => (state?.triggers || []).find((u) => u?.id === triggerId),
    [state?.triggers, triggerId]
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ToothFairyLoadingPlaceholder
            caption={t("triggers.loadingTriggers")}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        <AgentBuilderSidebar
          groups={sectionGroups}
          activeSection={activeSection}
          onSelect={setActiveSection}
          errorSections={errorSections}
        />
        <ScrollView
          ref={scrollRef}
          style={{ flex: 1, minWidth: 0 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <TriggersSettings
            key={triggerId}
            ref={formRef}
            initialMapValues={initialMapValues}
            isEdit={isEditMode}
            selectedId={isEditMode ? triggerId : undefined}
            opt={isEditMode ? selectedTrigger : undefined}
            settingType="trigger"
            onTriggerCreated={handleTriggerCreated}
            onTriggerDeleted={() =>
              history.push(`/workspaces/${workspaceId}/settings/triggers`)
            }
            isBuilderLayout
            builderActiveSection={activeSection}
            onBuilderContextChange={setBuilderCtx}
            saveVersion={saveVersion}
          />
        </ScrollView>
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(`/workspaces/${workspaceId}/settings/triggers`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("settings.triggers")}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.name || selectedTrigger?.name || ""
            : t("triggers.builderNewTrigger", { defaultValue: "New trigger" })}
        </AppText>
        <View style={{ flex: 1 }} />
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("triggers.unsavedChangesSubCaption", {
          defaultValue:
            "This trigger has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default TriggerBuilderScreenContainer;
